// src/pages/EditPurchase.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Toolbar,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../contexts/firebase/config';

export default function EditPurchase() {
  const { compraId } = useParams();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);

  const [fields, setFields] = useState({
    empresaId: '',
    clienteId: '',
clienteNome: '',
    dataCompra: '',     // yyyy-MM-dd
    valor: '',
    comissao: '',
    status: 'Aberto',
    percComissao: 0,    // % original obtida da compra
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Carrega a compra e extrai a percentagem
  useEffect(() => {
    async function fetchCompra() {
      setLoading(true);
      try {
        const ref = doc(db, 'compras', compraId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('Compra não encontrada.');
        } else {
          const data = snap.data();
          const qClientes = query(
  collection(db, "clientes"),
  where("empresaId", "==", data.empresaId)
);

const clientesSnap = await getDocs(qClientes);

const listaClientes = clientesSnap.docs.map(doc => ({
  id: doc.id,
  ...doc.data(),
}));

setClientes(listaClientes);
          const dtCompra = data.dataCompra?.toDate() ?? new Date();

const dataFormatada = dtCompra
  .toISOString()
  .split("T")[0];
          const valorNum = data.valor ?? 0;
          const comissaoNum = data.comissao ?? 0;
          const perc = valorNum > 0 ? comissaoNum / valorNum : 0;

          setFields({
            empresaId:    data.empresaId || '',
            clienteId: data.clienteId || '',
clienteNome: data.clienteNome || '',
            dataCompra: dataFormatada,
            valor:        valorNum.toString(),
            comissao:     comissaoNum.toString(),
            status:       data.status || 'Aberto',
            percComissao: perc,
          });
        }
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar dados da compra.');
      }
      setLoading(false);
    }
    fetchCompra();
  }, [compraId]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFields(f => ({ ...f, [name]: value }));
  };

  // Comisão preview (apenas display)
  const comissaoPreview = useMemo(() => {
    const v = parseFloat(fields.valor || '0');
    if (Number.isNaN(v)) return '';
    const c = (v * (fields.percComissao || 0)).toFixed(2);
    return c;
  }, [fields.valor, fields.percComissao]);

  // Salva respeitando a % original
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    const { empresaId, clienteNome, dataCompra, valor, status, percComissao } = fields;

    if (!clienteNome.trim() || !dataCompra || !valor || !status) {
      setError('Preencha Cliente, Ano, Valor e Status.');
      return;
    }

    setSaving(true);
    try {
      const ref = doc(db, 'compras', compraId);
      const valorNum = parseFloat(valor);
      const comissaoNum = parseFloat((valorNum * percComissao).toFixed(2));
      const tsCompra = Timestamp.fromDate(
  new Date(dataCompra)
);
      const clienteSelecionado = clientes.find(
    c => c.id === fields.clienteId
);

const updateObj = {
        clienteId: clienteSelecionado.id,
clienteNome: clienteSelecionado.nome,
clienteEstado: clienteSelecionado.estado,
clientePlano: clienteSelecionado.plano,
        dataCompra:  tsCompra,
        valor:       valorNum,
        comissao:    comissaoNum,
        status,
      };

      // Se marcou como pago, grava dataPagamento no início do ano
      if (status === 'Pago') {
        updateObj.dataPagamento = Timestamp.fromDate(
  new Date(dataCompra)
);
      }

      await updateDoc(ref, updateObj);
      navigate(`/admin/empresa/${empresaId}/compras`);
    } catch (err) {
      console.error(err);
      setError('Falha ao salvar alterações.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 5 }}>
      <Toolbar disableGutters sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to={`/admin/empresa/${fields.empresaId}/compras`}
          startIcon={<ArrowBackIcon />}
        >
          Voltar a Compras
        </Button>
      </Toolbar>

      <Card elevation={1}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h5" align="center" fontWeight={700} gutterBottom>
            ✏️ Editar Compra
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Actualize os dados. A comissão é recalculada com base na percentagem original.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }}>
            <FormControl fullWidth required>

    <InputLabel>Cliente</InputLabel>

    <Select
        name="clienteId"
        value={fields.clienteId}
        label="Cliente"
        onChange={(e) => {

            const cliente = clientes.find(
                c => c.id === e.target.value
            );

            setFields(prev => ({
                ...prev,
                clienteId: cliente.id,
                clienteNome: cliente.nome,
            }));

        }}
    >

        {clientes.map(cliente => (

            <MenuItem
                key={cliente.id}
                value={cliente.id}
            >
                {cliente.nome}
            </MenuItem>

        ))}

    </Select>

</FormControl>

            <TextField
  label="Data da Compra"
  name="dataCompra"
  type="date"
  value={fields.dataCompra}
  onChange={handleChange}
  InputLabelProps={{
    shrink: true,
  }}
  required
  fullWidth
/>

            <TextField
              label="Valor (€)"
              name="valor"
              type="number"
              inputProps={{ step: '0.01' }}
              value={fields.valor}
              onChange={handleChange}
              required
              fullWidth
            />

            <TextField
              label="Comissão (€)"
              name="comissao"
              value={comissaoPreview || fields.comissao}
              helperText="Calculada automaticamente a partir do valor e da % original"
              disabled
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={fields.status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="Aberto">Aberto</MenuItem>
                <MenuItem value="Pago">Pago</MenuItem>
                <MenuItem value="Cancelado">Cancelado</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ py: 1.3, px: 6 }}
              >
                {saving ? 'A guardar...' : 'Guardar Alterações'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
